import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Chip
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport
} from "@mui/x-data-grid";
import { useSearchParams } from "react-router-dom";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { DIRECTION_OPTIONS } from "../../constants/spendingAggregations";
import { useFetchSpendingAggregations } from "./hooks/useFetchSpendingAggregations";

const columns = [
  { field: "id", headerName: "ID", width: 80 },
  { field: "name", headerName: "Name", width: 220, flex: 1 },
  {
    field: "direction",
    headerName: "Direction",
    width: 130,
    renderCell: (params) => (
      <Chip
        label={params.value}
        size="small"
        color={params.value === "reduce" ? "primary" : "success"}
        variant="outlined"
      />
    )
  },
  {
    field: "type",
    headerName: "Type",
    width: 130,
    renderCell: (params) => (
      <Chip
        label={params.value === "auto_opt_in" ? "auto opt-in" : params.value}
        size="small"
        color={params.value === "auto_opt_in" ? "warning" : "default"}
        variant="outlined"
      />
    )
  },
  {
    field: "criteria",
    headerName: "Criteria",
    width: 260,
    flex: 1,
    sortable: false,
    valueGetter: (value) =>
      Object.entries(value ?? {})
        .map(([key, val]) => `${key}: ${JSON.stringify(val)}`)
        .join(", ")
  },
  {
    field: "hidden",
    headerName: "Hidden",
    width: 100,
    renderCell: (params) =>
      params.value ? <Chip label="hidden" size="small" variant="outlined" /> : null
  },
  {
    field: "shouldPreview",
    headerName: "Preview",
    width: 100,
    renderCell: (params) =>
      params.value
        ? <Chip label="preview" size="small" variant="outlined" color="primary" />
        : null
  },
  {
    field: "archived",
    headerName: "Status",
    width: 110,
    renderCell: (params) => (
      <Chip
        label={params.value ? "Archived" : "Active"}
        size="small"
        color={params.value ? "default" : "success"}
        variant={params.value ? "outlined" : "filled"}
      />
    )
  },
  {
    field: "updatedAt",
    headerName: "Updated",
    width: 160,
    valueFormatter: (value) =>
      value ? new Date(value).toLocaleDateString() : ""
  }
];

const Toolbar = ({ onNewClick }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [nameInput, setNameInput] = useState(searchParams.get("name") ?? "");

  const applyName = () => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      name: nameInput
    });
  };

  return (
    <GridToolbarContainer sx={{ mb: 1, gap: 2 }}>
      <FormControl sx={{ minWidth: 160 }} size="small">
        <InputLabel id="direction-filter-label">Direction</InputLabel>
        <Select
          labelId="direction-filter-label"
          label="Direction"
          value={searchParams.get("direction") ?? "All"}
          onChange={(event) =>
            setSearchParams({
              ...Object.fromEntries(searchParams.entries()),
              direction: event.target.value
            })
          }
        >
          <MenuItem value="All">All</MenuItem>
          {DIRECTION_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Search by name"
        size="small"
        value={nameInput}
        onChange={(event) => setNameInput(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            applyName();
          }
        }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={searchParams.get("showArchived") !== "false"}
            onChange={(event) =>
              setSearchParams({
                ...Object.fromEntries(searchParams.entries()),
                showArchived: String(event.target.checked)
              })
            }
          />
        }
        label="Show archived"
      />

      <Box sx={{ flexGrow: 1 }} />

      <GridToolbarExport
        slotProps={{
          tooltip: { title: "Export data" },
          button: { variant: "outlined" }
        }}
      />
      <Button variant="contained" startIcon={<AddIcon />} onClick={onNewClick}>
        New aggregation
      </Button>
    </GridToolbarContainer>
  );
};

export const SpendingAggregationList = ({ onRowClick, onNewClick }) => {
  const [searchParams] = useSearchParams();

  const direction = searchParams.get("direction") ?? "All";
  const name = searchParams.get("name") ?? "";
  const showArchived = searchParams.get("showArchived") !== "false";

  const { data, isLoading, refetch } = useFetchSpendingAggregations({
    direction,
    name
  });

  useEffect(() => {
    refetch();
  }, [searchParams, refetch]);

  if (isLoading) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }

  const rows = (data?.aggregations ?? []).filter(
    (row) => showArchived || !row.archived
  );

  return (
    <Box display="flex" flexDirection="column" gap="32px">
      <DataGrid
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        loading={isLoading}
        onRowClick={onRowClick}
        slots={{ toolbar: Toolbar }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton"
          },
          toolbar: { onNewClick }
        }}
        getRowClassName={(params) =>
          params.row.archived ? "row-archived" : ""
        }
        sx={{
          "& .row-archived": { opacity: 0.5 }
        }}
      />
    </Box>
  );
};
